import { DataLayer } from '@piwikpro/react-piwik-pro';
import React from 'react';

import { graphql, PageProps } from 'gatsby';

import { Grid2 as Grid, styled } from '@mui/material';

import { LeadIntent } from '@mewa/types';
import { MewaCircle, ResponsiveGrid, setARIATranslations } from '@mewa/ui';

import t from '../../../libs/translate';
import { NavigationData } from '../../components/header/types';
import { ServiceSegmentsOverviewWrapper } from '../../components/home/service-segments-overview-wrapper';
import {
  ComparisonTableData,
  StrapiComparisonTable,
} from '../../components/strapi/comparison-table';
import { StageVideo } from '../../components/strapi/stage-video';
import { GlobalPageContext } from '../../types';
import { storeLeadIntent } from '../conversion/persistence';
import {
  AccordionData,
  StrapiMarkdownAccordion,
} from '../dynamic-content-page/content-components/accordion';
import {
  ConversionTeaserImageData,
  StrapiConversionTeaserImage,
} from '../dynamic-content-page/content-components/conversion-teaser-image';
import { SharedMewaCircleData } from '../dynamic-content-page/content-components/shared-mewa-circle';
import { ResponsiveVideoData } from '../dynamic-content-page/content-components/video/types';

export { Head } from '../../layouts/head';

type Props = NavigationData & {
  strapiFullServicePage: {
    stageHeadline: string;
    stageSubline: string;
    video: ResponsiveVideoData;
    introHeadline: string;
    introTextLeft: string;
    introTextRight: string;
    mewaCircleHeadline: string;
    mewaCircleDescription: string;
    accordionTitle: string;
    accordionSubTitle: string;
    accordion: AccordionData | null;
    circle: SharedMewaCircleData['circle'];
    conversionTeaser: ConversionTeaserImageData | null;
    comparisonTable: ComparisonTableData;
  };
  translations: {
    general_form_button_label_back: string;
    general_form_button_label_next: string;
    label_play: string;
    label_pause: string;
    fullservice_page_product_overview_headline: string;
  };
};

type PageContext = GlobalPageContext;

export const query = graphql`
  query ($id: String!, $lang: String!, $locale: String!) {
    ...NavigationFragment

    strapiFullServicePage(id: { eq: $id }) {
      stageHeadline
      stageSubline
      video {
        ...ResponsiveVideoFragment
      }
      introHeadline
      introTextLeft
      introTextRight
      mewaCircleHeadline
      mewaCircleDescription
      accordionTitle
      accordionSubTitle
      accordion {
        ...MarkdownAccordionFragment
      }
      circle {
        ...MewaCircleFragment
      }

      conversionTeaser {
        ...ConversionTeaserImageFragment
      }
      comparisonTable {
        ...ComparisonTableFragment
      }
    }

    translations: translation(lang: { eq: $lang }) {
      general_form_button_label_back
      general_form_button_label_next
      label_play
      label_pause
      fullservice_page_product_overview_headline
    }
  }
`;

export const FullService = ({
  data: {
    navigationLinks,
    headerAndFooterTranslations,
    navigationClothing: { nodes: clothingServiceSegments },
    navigationNonClothing: { nodes: nonClothingServiceSegments },
    strapiFullServicePage,
    translations,
  },
  pageContext: { langPrefix, locale, name },
}: PageProps<Props, PageContext>) => {
  const conversionCta = {
    text: strapiFullServicePage.conversionTeaser?.conversionCta.text ?? '',
    url: `${langPrefix}/${navigationLinks.conversion.contact.slug}`,
  };

  setARIATranslations({
    label_back: translations.general_form_button_label_back,
    label_next: translations.general_form_button_label_next,
    label_play: translations.label_play,
    label_pause: translations.label_pause,
  });

  return (
    <>
      <div
        data-pagefind-meta={`title:${name}`}
        style={{ visibility: 'hidden' }}
      />

      <StageVideo
        data={{
          headline: strapiFullServicePage.stageHeadline,
          subline: strapiFullServicePage.stageSubline,
          video: strapiFullServicePage.video,
        }}
      />

      <IntroContainer>
        <Grid size={12}>
          <IntroHeadline>{strapiFullServicePage.introHeadline}</IntroHeadline>
        </Grid>
        <TextContainer size={{ xs: 12, lg: 10 }} offset={{ xs: 0, lg: 1 }}>
          <IntroText>{strapiFullServicePage.introTextLeft}</IntroText>
          <IntroText>{strapiFullServicePage.introTextRight}</IntroText>
        </TextContainer>
      </IntroContainer>

      {strapiFullServicePage.circle ? (
        <CircleContainer>
          <CircleTextContainer
            size={{ xs: 12, sm: 10, md: 8, lg: 6 }}
            offset={{ xs: 0, sm: 1, md: 2, lg: 3 }}
          >
            <CircleHeadline>
              {strapiFullServicePage.mewaCircleHeadline}
            </CircleHeadline>
            <p>{strapiFullServicePage.mewaCircleDescription}</p>
          </CircleTextContainer>
          <Grid size={12}>
            <StyledMewaCircle circle={strapiFullServicePage.circle} />
          </Grid>
        </CircleContainer>
      ) : null}

      {strapiFullServicePage.conversionTeaser && (
        <StrapiConversionTeaserImage
          data={{ ...strapiFullServicePage.conversionTeaser, conversionCta }}
          onClick={() => {
            storeLeadIntent(LeadIntent.consultation);
          }}
          piwikEventNameCTA="cta_full_service_page_teaser"
        />
      )}

      <StyledStrapiComparisonTable
        data={strapiFullServicePage.comparisonTable}
        cta={{
          onClick: () => {
            DataLayer.push({
              event: 'cta_full_service_page_advantages',
            });
          },
          url: `${langPrefix}/${navigationLinks.conversion.contact.slug}`,
        }}
      />

      {strapiFullServicePage.accordion ? (
        <AccordionContainer>
          <ResponsiveGrid>
            <Grid size={12}>
              <AccordionHeadline>
                {strapiFullServicePage.accordionTitle}
              </AccordionHeadline>
              <AccordionSubTitle>
                {strapiFullServicePage.accordionSubTitle}
              </AccordionSubTitle>
            </Grid>
          </ResponsiveGrid>
          <StrapiMarkdownAccordion {...strapiFullServicePage.accordion} />
        </AccordionContainer>
      ) : null}

      <ServiceSegmentsWrapper>
        <ServiceSegmentsOverviewWrapper
          clothingServiceSegments={clothingServiceSegments}
          nonClothingServiceSegments={nonClothingServiceSegments}
          langPrefix={langPrefix}
          headerAndFooterTranslations={headerAndFooterTranslations}
          navigationLinks={navigationLinks}
          headline={t(
            translations,
            'fullservice_page_product_overview_headline',
          )}
          theme={'white'}
        />
      </ServiceSegmentsWrapper>
    </>
  );
};

FullService.noPhoneContact = true;

export default FullService;

const AccordionContainer = styled('div')(({ theme }) => ({
  marginTop: '60px',
  marginBottom: '70px',

  [theme.breakpoints.up('md')]: {
    marginTop: '70px',
    marginBottom: '80px',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '80px',
    marginBottom: '100px',
  },
}));

const AccordionHeadline = styled('h2')(({ theme }) => ({
  ...theme.typography.h2,
  textAlign: 'center',
  margin: '0 0 20px',
}));

const AccordionSubTitle = styled('h3')(({ theme }) => ({
  ...theme.typography.h5,
  textAlign: 'center',
  margin: '0 0 60px',
}));

const CircleHeadline = styled('h3')(({ theme }) => ({
  ...theme.typography.h3,
  margin: '0 0 30px',

  [theme.breakpoints.up('sm')]: {
    ...theme.typography.h4,
    textAlign: 'center',
  },
}));

const StyledMewaCircle = styled(MewaCircle)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    marginTop: '42px',
  },

  [theme.breakpoints.up('mdSm')]: {
    marginTop: '94px',
  },
}));

const StyledStrapiComparisonTable = styled(StrapiComparisonTable)(
  ({ theme }) => ({
    marginTop: '60px',
    marginBottom: '70px',

    [theme.breakpoints.up('md')]: {
      marginTop: '70px',
      marginBottom: '80px',
    },

    [theme.breakpoints.up('lg')]: {
      marginTop: '80px',
      marginBottom: '100px',
    },
  }),
);

const IntroContainer = styled(ResponsiveGrid)(({ theme }) => ({
  marginTop: '60px',
  marginBottom: '70px',

  [theme.breakpoints.up('md')]: {
    marginTop: '70px',
    marginBottom: '80px',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '80px',
    marginBottom: '100px',
  },
}));

const TextContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--gutter)',

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

const CircleContainer = styled(ResponsiveGrid)(({ theme }) => ({
  marginTop: '60px',
  marginBottom: '70px',

  [theme.breakpoints.up('md')]: {
    marginTop: '70px',
    marginBottom: '80px',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '80px',
    marginBottom: '100px',
  },
}));

const CircleTextContainer = styled(Grid)(({ theme }) => ({
  p: {
    ...theme.typography.body,
    margin: '0 0 30px',
  },
}));

const IntroHeadline = styled('h2')(({ theme }) => ({
  ...theme.typography.h2,
  marginTop: 0,
  marginBottom: '30px',

  [theme.breakpoints.up('sm')]: {
    textAlign: 'center',
  },
}));

const IntroText = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  margin: 0,
}));

const ServiceSegmentsWrapper = styled('div')(({ theme }) => ({
  marginTop: '60px',
  marginBottom: '70px',

  [theme.breakpoints.up('md')]: {
    marginTop: '70px',
    marginBottom: '80px',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '80px',
    marginBottom: '100px',
  },
}));
